// Tela cheia em tinta (DESIGN §2.7 z-70, §3.1): descanso, subiu de nível, QR da recepção, celebrações.
// Troca o theme-color para tinta enquanto montada; role="dialog" com foco no primeiro controle.
import { useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { useCorDoTema, useFocoPreso, useTravarRolagem } from "../lib/hooks";

export interface PropsTelaCheia {
  /** Nome acessível do diálogo (pode não estar visível). */
  rotulo: string;
  /** Esc / fechar. Omitir quando a tela só fecha por ação explícita ("Continuar"). */
  aoFechar?: () => void;
  children: ReactNode;
  /** "tinta" (padrão) ou "parede" (celebrações claras; no app do aluno, a parede escura). */
  tom?: "tinta" | "parede";
  /**
   * theme-color enquanto montada. Padrão: o do tom no tema claro. No app do aluno (tema escuro), a parede é #0C0D0F
   * (Recorte da foto de perfil, docs/prototipo/foto-perfil-recorte.html §6).
   */
  corTema?: string;
  className?: string;
}

export function TelaCheia({ rotulo, aoFechar, children, tom = "tinta", corTema, className }: PropsTelaCheia) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  useCorDoTema(corTema ?? (tom === "tinta" ? "#141414" : "#EDEEF0"));
  useFocoPreso(ref, true, aoFechar);
  useTravarRolagem(true);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={id}
      tabIndex={-1}
      className={cn(
        "fixed inset-0 z-70 flex min-h-dvh flex-col overflow-y-auto pt-safe pr-safe pb-safe pl-safe animate-fade-entra focus:outline-none",
        tom === "tinta" ? "bg-tinta text-sobre-tinta" : "bg-parede text-tinta",
        className,
      )}
    >
      <span id={id} className="sr-only">
        {rotulo}
      </span>
      {children}
    </div>,
    document.body,
  );
}
